// src/pages/DriverDocsPortalPublic.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { db, storage } from "../firebase/firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { sha256Hex } from "../utils/crypto";

const DOCS = [
  { fileKeys: ["passportFile", "passportFileBack"], expiryKey: "passportExpiry", label: "Passport", pageLabels: ["Page 1", "Page 2"] },
  { fileKeys: ["emiratesIdFile", "emiratesIdFileBack"], expiryKey: "emiratesIdExpiry", label: "Emirates ID", pageLabels: ["Front", "Back"] },
  { fileKeys: ["licenseFile", "licenseFileBack"], expiryKey: "licenseExpiry", label: "Driving License", pageLabels: ["Front", "Back"] },
  { fileKeys: ["rtaPermitFile"], expiryKey: "rtaPermitExpiry", label: "RTA Permit", pageLabels: ["File"] },
  { fileKeys: ["rtaTrainingFile"], expiryKey: "rtaTrainingExpiry", label: "RTA Training", pageLabels: ["File"] },
  { fileKeys: ["healthInsuranceFile"], expiryKey: "healthInsuranceExpiry", label: "Health Insurance", pageLabels: ["File"] },
];

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function fmtDate(v) {
  if (!v) return "";
  // stored as YYYY-MM-DD already in this project
  if (typeof v === "string") return v.slice(0, 10);
  // Firestore timestamp
  if (v?.seconds) {
    const d = new Date(v.seconds * 1000);
    return d.toISOString().slice(0, 10);
  }
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

async function uploadToStorage({ file, token, codeHash, docKey }) {
  const safeName = String(file.name || "file").replace(/[^\w.\-()]/g, "_");
  const path = `driverSubmissions/${token}/${codeHash}/${docKey}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, path);

  const metadata = {
    contentType: file.type || "application/octet-stream",
  };

  await uploadBytes(storageRef, file, metadata);
  return await getDownloadURL(storageRef);
}

export default function DriverDocsPortalPublic() {
  const query = useQuery();
  const token = query.get("token") || "";

  const [invite, setInvite] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [codeHash, setCodeHash] = useState("");

  const codeRef = useRef(null);

  const [submission, setSubmission] = useState(null);
  // Keep the live-listener cleanup in a ref so it is always current.
  // State caused stale cleanup callbacks when the same public route reopened.
  const submissionUnsubRef = useRef(null);

  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // ------------------------------
  // Load invite
  // ------------------------------
  useEffect(() => {
    let alive = true;

    // Always start a completely fresh portal session when the link opens or
    // the token changes. This removes stale verification/listener state when
    // the same SPA route is opened again without a hard browser refresh.
    try {
      submissionUnsubRef.current?.();
    } catch {}
    submissionUnsubRef.current = null;

    setInviteLoading(true);
    setInvite(null);
    setCode("");
    setCodeHash("");
    setVerified(false);
    setSubmission(null);
    setBusyKey("");
    setError("");
    setInfo("");

    (async () => {
      try {
        if (!token) {
          setError("Missing token. Please use the link shared by your company.");
          return;
        }
        const snap = await getDoc(doc(db, "driverDocInvites", token));
        if (!alive) return;
        if (!snap.exists()) {
          setError("This upload link is invalid or expired. Please ask admin to generate a new link.");
          return;
        }
        const data = snap.data();
        if (!data?.active) {
          setError("This upload link has been replaced. Please use the latest link sent by the office.");
          return;
        }

        // If a current-invite pointer exists for this employee, only that
        // token is accepted. This permanently prevents multiple active links.
        if (data?.driverId) {
          const pointerSnap = await getDoc(doc(db, "driverDocActiveInvites", data.driverId));
          if (pointerSnap.exists() && pointerSnap.data()?.token !== token) {
            setError("This upload link has been replaced. Please use the latest link sent by the office.");
            return;
          }
        }

        setInvite({ id: snap.id, ...data });
      } catch (e) {
        console.error(e);
        setError("Failed to load invite. Please try again.");
      } finally {
        if (alive) setInviteLoading(false);
      }
    })();

    return () => {
      alive = false;
      try {
        submissionUnsubRef.current?.();
      } catch {}
      submissionUnsubRef.current = null;
    };
  }, [token]);

  // ------------------------------
  // Verify code
  // ------------------------------
  const verify = async () => {
    setError("");
    setInfo("");
    if (!invite) return;
    if (!code.trim()) {
      setError("Please enter the secret code provided by admin.");
      return;
    }

    try {
      const h = await sha256Hex(`${token}:${code.trim()}`);
      if (h !== invite.codeHash) {
        setError("Incorrect code. Please double-check and try again.");
        return;
      }

      const refDoc = doc(db, "driverDocSubmissions", token, "submissions", h);

      // Create the draft only once. On later openings, keep the existing
      // submission unchanged. The transaction also handles two browsers
      // verifying the same valid link at the same time without a race.
      await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(refDoc);
        if (!existing.exists()) {
          transaction.set(refDoc, {
            status: "draft",
            token,
            codeHash: h,
            driverId: invite.driverId,
            driverName: invite.driverName || "",
            company: invite.company || "",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      });

      // Stop any listener left by a previous verification attempt before
      // attaching the listener for this clean session.
      try {
        submissionUnsubRef.current?.();
      } catch {}
      submissionUnsubRef.current = null;
      setSubmission(null);

      setCodeHash(h);
      setVerified(true);

      const unsub = onSnapshot(
        refDoc,
        (snap) => {
          setSubmission(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        },
        (listenerError) => {
          console.error("Submission listener failed:", listenerError);
          setError("The code is correct, but document access was blocked. Please contact the office.");
        }
      );
      submissionUnsubRef.current = unsub;

      setInfo("✅ Code verified. You can now upload documents and set expiry dates.");
    } catch (e) {
      console.error(e);
      setError("Failed to verify code. Please try again.");
    }
  };

  const subRef = verified
    ? doc(db, "driverDocSubmissions", token, "submissions", codeHash)
    : null;

  const currentStatus = String(submission?.status || "draft").toLowerCase();
  const locked = currentStatus === "approved";

  // ------------------------------
  // Update expiry
  // ------------------------------
  const updateExpiry = async (expiryKey, value) => {
    if (!subRef) return;
    setError("");
    setInfo("");
    try {
      await updateDoc(subRef, {
        [expiryKey]: value || null,
        status: currentStatus === "rejected" ? "draft" : currentStatus || "draft",
        updatedAt: serverTimestamp(),
      });
      setInfo("Saved.");
    } catch (e) {
      console.error(e);
      setError("Failed to save expiry. Please try again.");
    }
  };

  // ------------------------------
  // Upload file
  // ------------------------------
  const uploadFile = async (docKey, file) => {
    if (!subRef) return;
    if (!file) return;
    setError("");
    setInfo("");

    try {
      setBusyKey(docKey);
      const url = await uploadToStorage({ file, token, codeHash, docKey });
      await updateDoc(subRef, {
        [docKey]: url,
        status: currentStatus === "rejected" ? "draft" : currentStatus || "draft",
        updatedAt: serverTimestamp(),
      });
      setInfo("✅ Uploaded.");
    } catch (e) {
      console.error(e);
      setError("Upload failed. Please try again.");
    } finally {
      setBusyKey("");
    }
  };

  const submitToAdmin = async () => {
    if (!subRef) return;
    setError("");
    setInfo("");

    try {
      // do a light validation: at least one file or expiry filled
      const hasSomething = DOCS.some(
        (d) => d.fileKeys.some((key) => submission?.[key]) || submission?.[d.expiryKey]
      );
      if (!hasSomething) {
        setError("Please upload at least one document or set at least one expiry date before submitting.");
        return;
      }

      await updateDoc(subRef, {
        status: "submitted",
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setInfo("✅ Submitted to admin for approval.");
    } catch (e) {
      console.error(e);
      setError("Failed to submit. Please try again.");
    }
  };

  const Card = ({ children }) => (
    <div
      style={{
        maxWidth: 900,
        margin: "18px auto",
        padding: 18,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {children}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "24px 12px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0 }}>Employee Documents Upload</h2>
            <div style={{ color: "#6b7280", marginTop: 4, fontSize: 13 }}>
              Upload documents and set expiry dates. Only documents/expiry fields are editable here.
            </div>
          </div>
          {invite?.company ? (
            <div style={{ textAlign: "right", fontSize: 13, color: "#374151" }}>
              <div style={{ fontWeight: 700 }}>{invite.company}</div>
              <div style={{ color: "#6b7280" }}>{invite.driverName || ""}</div>
            </div>
          ) : null}
        </div>

        {inviteLoading ? (
          <div style={{ marginTop: 18 }}>Loading...</div>
        ) : error ? (
          <div style={{ marginTop: 18, color: "#b91c1c", fontWeight: 600 }}>{error}</div>
        ) : !verified ? (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontWeight: 600 }}>Enter the secret code:</div>
            <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              <input
                ref={codeRef}
                value={code}
                onChange={(e) => {
                  const v = String(e.target.value || "")
                    .replace(/\D/g, "")
                    .slice(0, 6);
                  setCode(v);
                  // Keep focus so the mobile keypad doesn't disappear after each digit
                  try {
                    requestAnimationFrame(() => codeRef.current?.focus());
                  } catch {}
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") verify();
                }}
                inputMode="numeric"
                pattern="[0-9]*"
                type="tel"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="6-digit code"
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontSize: 16,
                  width: 200,
                  letterSpacing: "0.25em",
                }}
              />
              <button
                onClick={verify}
                style={{
                  padding: "10px 14px",
                  border: "none",
                  borderRadius: 8,
                  background: "#111827",
                  color: "white",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Verify
              </button>
            </div>
            <div style={{ color: "#6b7280", fontSize: 13, marginTop: 10 }}>
              You received this code from your admin. If you don&apos;t have it, request it from the office.
            </div>
          </div>
        ) : (
          <>
            {submission?.rejectComment && currentStatus === "rejected" ? (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 10,
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#9a3412",
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: 4 }}>Rejected by admin</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{submission.rejectComment}</div>
                <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
                  Please upload again and click “Submit to admin”.
                </div>
              </div>
            ) : null}

            {locked ? (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 10,
                  background: "#ecfdf5",
                  border: "1px solid #a7f3d0",
                  color: "#065f46",
                  fontWeight: 700,
                }}
              >
                ✅ Approved by admin. No further action required.
              </div>
            ) : null}

            {info ? (
              <div style={{ marginTop: 14, color: "#065f46", fontWeight: 600 }}>{info}</div>
            ) : null}
            {error ? (
              <div style={{ marginTop: 14, color: "#b91c1c", fontWeight: 600 }}>{error}</div>
            ) : null}

            <div style={{ marginTop: 18 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Documents</div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                {DOCS.map((d) => {
                  const exp = fmtDate(submission?.[d.expiryKey]);
                  return (
                    <div
                      key={d.label}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.1fr 0.8fr 1.6fr",
                        gap: 12,
                        alignItems: "start",
                        padding: 12,
                        borderBottom: "1px solid #e5e7eb",
                        background: "white",
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{d.label}</div>

                      <div>
                        <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>Expiry date</div>
                        <input
                          type="date"
                          value={exp}
                          disabled={locked}
                          onChange={(e) => updateExpiry(d.expiryKey, e.target.value)}
                          style={{ padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, width: "100%" }}
                        />
                      </div>

                      <div style={{ display: "grid", gap: 10 }}>
                        {d.fileKeys.map((fileKey, index) => {
                          const url = submission?.[fileKey];
                          const pageLabel = d.pageLabels[index] || `File ${index + 1}`;
                          return (
                            <div key={fileKey}>
                              <div style={{ fontSize: 13, color: "#374151", marginBottom: 5 }}>{pageLabel}</div>
                              {url ? (
                                <a href={url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 13, color: "#2563eb", textDecoration: "none", marginBottom: 5 }}>
                                  View uploaded {pageLabel.toLowerCase()}
                                </a>
                              ) : null}
                              <input
                                type="file"
                                disabled={locked || busyKey === fileKey}
                                accept="image/*,application/pdf"
                                onChange={(e) => uploadFile(fileKey, e.target.files?.[0] || null)}
                              />
                              {busyKey === fileKey ? <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Uploading…</div> : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <button
                  onClick={submitToAdmin}
                  disabled={locked || currentStatus === "submitted"}
                  style={{
                    padding: "10px 14px",
                    border: "none",
                    borderRadius: 8,
                    background: currentStatus === "submitted" ? "#9ca3af" : "#059669",
                    color: "white",
                    fontWeight: 800,
                    cursor: locked || currentStatus === "submitted" ? "not-allowed" : "pointer",
                  }}
                >
                  {currentStatus === "submitted" ? "Submitted" : "Submit to admin"}
                </button>

                <div style={{ alignSelf: "center", fontSize: 13, color: "#6b7280" }}>
                  Status: <b>{currentStatus}</b>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>

      <div style={{ textAlign: "center", color: "#6b7280", fontSize: 12 }}>
        If you face issues opening this page, contact the office.
      </div>
    </div>
  );
}
